
import React from "react";
import { TableBody, TableRow, TableCell } from "@/components/ui/table";
import { ClassRow } from "./ClassRow";
import { ClassDetailsRow } from "./ClassDetailsRow";
import { EmptyStateRow } from "./EmptyStateRow";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from 'react-i18next';
import { BookingData } from "@/types/types";

interface TableContentProps {
  bookings: BookingData[];
  isLoading: boolean;
  getClassCapacity: (booking: BookingData) => number;
  attendeesData: {[key: string]: any[]};
  expandedRow: string | null;
  updatingAttendanceId: string | null;
  removingClientId: string | null;
  onToggleExpand: (id: string) => void;
  onMarkAttendance: (bookingId: string, attended: boolean, userName: string) => Promise<boolean>;
  onRemoveClient: (classId: string, userId: string, classDate: string, userName: string) => Promise<boolean>;
}

export const TableContent: React.FC<TableContentProps> = ({
  bookings,
  isLoading,
  getClassCapacity,
  attendeesData,
  expandedRow,
  updatingAttendanceId,
  removingClientId,
  onToggleExpand,
  onMarkAttendance,
  onRemoveClient
}) => {
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <TableBody>
        {[1, 2, 3, 4, 5].map((i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton className="h-4 w-32" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-20" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-16" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-6 w-20 rounded-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-8 w-8 rounded" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  }
  
  if (!bookings || bookings.length === 0) {
    return (
      <TableBody>
        <EmptyStateRow message={t('noData')}/>
      </TableBody>
    );
  }

  // Filter out duplicate bookings by creating a unique key for each booking
  // and using a Map to keep only one booking per unique combination of class, date, and time
  const uniqueBookingsMap = new Map();
  
  bookings.forEach(booking => {
    const uniqueKey = `${booking.className}_${booking.date}_${booking.time || '00:00'}`;
    
    // Only keep one booking per unique key (first one encountered)
    if (!uniqueBookingsMap.has(uniqueKey)) {
      uniqueBookingsMap.set(uniqueKey, booking);
    }
  });
  
  // Convert the Map back to an array
  const uniqueBookings = Array.from(uniqueBookingsMap.values());
  uniqueBookings.sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time || '00:00'}`);
    const dateB = new Date(`${b.date}T${b.time || '00:00'}`);
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <TableBody>
      {uniqueBookings.map((booking) => {
        const capacity = getClassCapacity(booking);
        // Include time in the key to differentiate classes at different times
        const key = `${booking.className}_${booking.date}_${booking.time || '00:00'}`;
        const attendees = attendeesData[key] || [];
        const attendeesCount = attendees.filter((att) => att.status !== "cancelled").length;
      
        const allCancelled = attendees.length > 0 && attendees.every(attendee => attendee.status === 'cancelled');
        const displayStatus = allCancelled ? 'cancelled' :
         attendees.length > 0 ? attendees.filter(attendee => attendee.status !== 'cancelled')[0].status
         :booking.status;

        return (
          <React.Fragment key={booking.id}>
            <ClassRow
              booking={booking}
              capacity={capacity}
              attendeesCount={attendeesCount}
              isExpanded={expandedRow === booking.id}
              onToggleExpand={() => onToggleExpand(booking.id)}
              status={displayStatus}
            />
            
            {expandedRow === booking.id && (
              <ClassDetailsRow
                attendees={attendees}
                classId={booking.id}
                classDate={booking.date}
                updatingAttendanceId={updatingAttendanceId}
                removingClientId={removingClientId}
                onMarkAttendance={onMarkAttendance}
                onRemoveClient={onRemoveClient}
              />
            )}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
};
